import { FormFieldType } from '../../../extViewmodels';
import type { CustomFormFieldOption, IFormField, IFormResponse } from '../../../extViewmodels';
import type { IMeme } from '../../viewModels/gamification';

/**
 * Per-field definition projected from the legacy `IFormField<T>` shape.
 *
 * The focused-view `DealField` renderer accepts this and a plain `value`
 * + `onChange` instead of the legacy MobX `FormFieldViewModel`. Stage 7
 * v1 covers the field-type subset we actually need on the Create Deal
 * form (per the screenshot: String, Note, DateTime, Option, User,
 * Contact); other types fall back to a placeholder. See `SPIKE.md`.
 */
export type DealFieldDef = {
	/**
	 * Server id of the field. Required to lazy-load `Option` /
	 * `MultipleOptions` choices via
	 * `lead/{companyId}/CreateDeal/Field/{id}` — the legacy
	 * `FormFieldViewModel.loadOptions` endpoint. Inline `options` from the
	 * top-level form response are normally empty for those field types; the
	 * choices live behind this per-field paginated endpoint.
	 */
	id: string;
	name: string;
	label: string;
	fieldType: FormFieldType;
	isOptional?: boolean;
	isHidden?: boolean;
	placeHolder?: string;
	/**
	 * Inline options. `IFormField.options` is rarely populated for
	 * `Option` / `MultipleOptions` fields (the server defers them to the
	 * per-field endpoint above). When present we use them directly; when
	 * empty, `DealField` lazy-loads them through `useDealFieldOptions`.
	 */
	options?: { id: string; label: string }[];
	/** Server-supplied initial value. */
	initialValue?: unknown;
	maxLength?: number;
	decimalPlaces?: number;
};

export type DealFormConfig = {
	/** Form-level metadata (name, description, etc.) — projected from IFormResponse. */
	name?: string;
	description?: string;
	fields: DealFieldDef[];
};

export type DealSubmitPayload = {
	/**
	 * Field id → value, nested under `values` when POSTed — the legacy
	 * `IFormRequest` contract (`aida/viewModels/form.ts:70-73,550`). Keyed by
	 * `field.id` (never `field.name`, which is optional and can collide). Note
	 * fields carry the saved note's id; User/Contact/Option carry the chosen
	 * `.id`; Date/DateTime carry a `moment().format()` string.
	 */
	values: Record<string, unknown>;
	/**
	 * Scheduled-meeting id from the auto demo scheduler. Mutually exclusive with
	 * the demo-perform-date value (legacy sends one or the other —
	 * `form.ts:521-548`).
	 */
	meetingId?: string;
	/**
	 * Phone call the deal is being created from, so the backend links the deal
	 * to the call. Mirrors `DealDrawerContent.onActionClick`'s
	 * `customFormData.phoneCallId` (`DealDrawerContent/index.tsx:85-88`).
	 */
	phoneCallId?: string;
	/** Lead-served source passthrough (legacy `ISkipLeadRequest.leadServedSource`). */
	leadServedSource?: string;
	/** Optional pass-through extras (e.g. decisionMaker context the legacy passes). */
	customProperties?: Record<string, unknown>;
};

export type CreateDealResult = {
	dealId?: string;
	meme?: IMeme;
};

const NOTE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A `Note` field value is either a saved note's id (from the Update Deal schema) or editor HTML. */
export function isSavedNoteId(value: unknown): value is string {
	return typeof value === 'string' && NOTE_ID_RE.test(value);
}

/**
 * Project an `IFormResponse<unknown>` into the cleaner `DealFormConfig`. Hidden
 * fields are kept in the config so the form can submit them with their
 * server-default values.
 */
export function fromFormResponse(response: IFormResponse<unknown>): DealFormConfig {
	return {
		name: response.name,
		description: response.description,
		fields: (response.fields ?? []).map(projectField),
	};
}

function projectField(field: IFormField<unknown>): DealFieldDef {
	return {
		id: field.id ?? '',
		name: field.name ?? '',
		label: field.label,
		fieldType: field.fieldType,
		isOptional: field.isOptional,
		isHidden: field.isHidden,
		placeHolder: field.placeHolder,
		options: field.options?.map(projectOption),
		initialValue: projectInitialValue(field.fieldType, field.value),
		maxLength: field.maxLength,
		decimalPlaces: field.decimalPlaces,
	};
}

/**
 * For `Option` / `MultipleOptions` / `User` / `Contact`, the server returns
 * the selected value as the full `IFormFieldOption | IUser | IContact`
 * object — not a string id. Legacy `FormFieldViewModel.save` reads `.id`
 * at submit time (see `aida/viewModels/form.ts:496`). The focused-view
 * DealField stores ids as strings throughout, so we extract `.id` at
 * projection time so the controlled Select / Combobox value is always a
 * string and the trigger renders the option's label (not `[object Object]`).
 */
function projectInitialValue(fieldType: FormFieldType, value: unknown): unknown {
	if (value == null) return value;
	switch (fieldType) {
		case FormFieldType.Option:
		case FormFieldType.User:
		case FormFieldType.Contact:
			if (typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
				return (value as { id?: string }).id ?? '';
			}
			return value;
		case FormFieldType.MultipleOptions:
			if (Array.isArray(value)) {
				return value
					.map(v =>
						v && typeof v === 'object' && 'id' in (v as Record<string, unknown>)
							? ((v as { id?: string }).id ?? '')
							: typeof v === 'string'
								? v
								: ''
					)
					.filter(Boolean)
					.join(',');
			}
			return value;
		default:
			return value;
	}
}

function projectOption(option: CustomFormFieldOption): { id: string; label: string } {
	return {
		id: option.id ?? '',
		label: option.label ?? '',
	};
}
